/**
 * lesionVolume.js — объём очага от одной точки.
 *
 * Зачем. В динамике важен не поиск опухоли — врач знает, где она, — а
 * измерение одного и того же одинаково дважды. «Было столько-то кубических
 * миллиметров, стало столько-то» отвечает на вопрос, ради которого делают
 * повторную съёмку. На глаз этот вопрос не решается: очаг растёт медленно, а
 * два снимка сняты под разным углом и с разной яркостью.
 *
 * Модуль чистый: ни импортов, ни Cornerstone, ни DOM. На вход — воксели,
 * размеры и шаг; на выход — число. Поэтому его можно проверить арифметикой на
 * шаре известного радиуса, а не «посмотреть, похоже ли».
 *
 * Способ простой и объяснимый: рост области от точки по коридору яркости.
 * Нейросети здесь намеренно нет. Врачу важно понимать, ПОЧЕМУ получилось это
 * число, и уметь его поправить; догадка сети такого не даёт, а ошибается она
 * молча и убедительно.
 */

/**
 * Вырастить область от точки-семени.
 *
 * @param {{data: ArrayLike<number>, dims: number[], spacing: number[]}} том
 * @param {number[]} семя — индексы [i, j, k]
 * @param {{коридор?: number, доляОтСемени?: number, максМм?: number}} [o]
 *   коридор       — допуск яркости в единицах значения; если не задан,
 *                   берётся доля от значения семени;
 *   доляОтСемени  — какую долю значения семени считать допуском (0.35 по умолч.);
 *   максМм        — не уходить дальше этого радиуса от семени (по умолчанию 30).
 * @returns {{вокселей: number, объёмМм3: number, среднее: number,
 *            центр: number[], габарит: number[], упёрлись: boolean} | null}
 */
/**
 * Уточнить семя: взять самый яркий воксель в нескольких миллиметрах от
 * указанной точки.
 *
 * Зачем. Врач щёлкает по очагу на глаз, и попасть точно в него нельзя — да и
 * не нужно. А во втором исследовании точка приходит перенесённой, и перенос
 * никогда не идеален. Промах в пару миллиметров означает, что семя село на
 * окружающую ткань, рост ушёл по ней во весь мозг, и число получилось
 * бессмысленным. Проверено вживую: 428 мм³ в первом исследовании против
 * 96474 мм³ во втором — просто потому, что семя село мимо.
 *
 * Правило простое и объяснимое: самая яркая точка рядом. Для очага, который
 * накапливает контраст, это он и есть. Для тёмного очага правило неверно —
 * поэтому его можно выключить (уточнитьПоЯркому: 0).
 */
function _уточнитьСемя(том, семя, радиусМм) {
  const [nx, ny, nz] = том.dims;
  const [sx, sy, sz] = том.spacing || [1, 1, 1];
  const [i0, j0, k0] = семя;
  const ri = Math.max(0, Math.round(радиусМм / sx));
  const rj = Math.max(0, Math.round(радиусМм / sy));
  const rk = Math.max(0, Math.round(радиусМм / sz));
  let лучший = семя, максЗнач = -Infinity;
  for (let k = k0 - rk; k <= k0 + rk; k++) {
    if (k < 0 || k >= nz) continue;
    for (let j = j0 - rj; j <= j0 + rj; j++) {
      if (j < 0 || j >= ny) continue;
      for (let i = i0 - ri; i <= i0 + ri; i++) {
        if (i < 0 || i >= nx) continue;
        const v = том.data[i + nx * (j + ny * k)];
        if (v > максЗнач) { максЗнач = v; лучший = [i, j, k]; }
      }
    }
  }
  return лучший;
}

export function growLesion(том, семя, o = {}) {
  if (!том?.data || !Array.isArray(том.dims) || том.dims.length !== 3) return null;
  const [nx, ny, nz] = том.dims;
  const [sx, sy, sz] = том.spacing || [1, 1, 1];
  const [i0, j0, k0] = (семя || []).map((v) => Math.round(v));
  if (!(i0 >= 0 && i0 < nx && j0 >= 0 && j0 < ny && k0 >= 0 && k0 < nz)) return null;

  const данные = том.data;
  const адрес = (i, j, k) => i + nx * (j + ny * k);

  // Уточняем семя, если не запрещено: см. пояснение у _уточнитьСемя.
  const радиусУточнения = o.уточнитьПоЯркому ?? 3;
  const [ui, uj, uk] = радиусУточнения > 0
    ? _уточнитьСемя({ data: данные, dims: том.dims, spacing: том.spacing }, [i0, j0, k0], радиусУточнения)
    : [i0, j0, k0];
  const значСемени = данные[адрес(ui, uj, uk)];
  // [10.19.1] NaN-семя — отказ, а не «объём в один воксель»: коридор от NaN
  // не имеет смысла, и число получилось бы уверенным и пустым.
  if (!Number.isFinite(значСемени)) return null;
  const доля = o.доляОтСемени ?? 0.35;
  const коридор = o.коридор ?? Math.max(1, Math.abs(значСемени) * доля);
  const низ = значСемени - коридор;
  const верх = значСемени + коридор;
  const максМм = o.максМм ?? 30;

  // Помечаем побывавшие вокселы отдельным полем, а не значением в данных:
  // данные чужие, и портить их нельзя.
  const был = new Uint8Array(nx * ny * nz);
  const очередь = [adrPack(ui, uj, uk)];
  был[адрес(ui, uj, uk)] = 1;

  let вокселей = 0, сумма = 0;
  let ci = 0, cj = 0, ck = 0;
  let minI = ui, maxI = ui, minJ = uj, maxJ = uj, minK = uk, maxK = uk;
  let упёрлись = false;

  function adrPack(i, j, k) { return (k * ny + j) * nx + i; }

  while (очередь.length) {
    const p = очередь.pop();
    const i = p % nx;
    const j = ((p - i) / nx) % ny;
    const k = ((p - i) / nx - j) / ny;

    const v = данные[p];
    вокселей++; сумма += v;
    ci += i; cj += j; ck += k;
    if (i < minI) minI = i; if (i > maxI) maxI = i;
    if (j < minJ) minJ = j; if (j > maxJ) maxJ = j;
    if (k < minK) minK = k; if (k > maxK) maxK = k;

    for (let d = 0; d < 6; d++) {
      const ni = i + (d === 0 ? 1 : d === 1 ? -1 : 0);
      const nj = j + (d === 2 ? 1 : d === 3 ? -1 : 0);
      const nk = k + (d === 4 ? 1 : d === 5 ? -1 : 0);
      if (ni < 0 || ni >= nx || nj < 0 || nj >= ny || nk < 0 || nk >= nz) continue;
      const a = adrPack(ni, nj, nk);
      if (был[a]) continue;
      const зн = данные[a];
      // [10.19.1] Явная проверка «внутри коридора»: для NaN оба сравнения
      // «меньше низа» и «больше верха» ложны, и NaN-воксели тихо попадали в
      // область, делая «среднее» NaN и позволяя расти сквозь NaN-карманы.
      if (!(зн >= низ && зн <= верх)) continue;
      // Ограничение по расстоянию: без него рост уходит по сосудам и оболочкам
      // на весь мозг, и число теряет смысл.
      const dмм = Math.hypot((ni - ui) * sx, (nj - uj) * sy, (nk - uk) * sz);
      if (dмм > максМм) { упёрлись = true; continue; }
      был[a] = 1;
      очередь.push(a);
    }
  }

  const объёмВокселя = Math.abs(sx * sy * sz);
  return {
    вокселей,
    объёмМм3: вокселей * объёмВокселя,
    среднее: вокселей ? сумма / вокселей : 0,
    центр: [ci / вокселей, cj / вокселей, ck / вокселей],
    габарит: [
      (maxI - minI + 1) * sx,
      (maxJ - minJ + 1) * sy,
      (maxK - minK + 1) * sz,
    ],
    упёрлись,
  };
}

/**
 * Насколько изменился объём. Отдельной функцией, потому что «на сколько
 * процентов» врачи считают по-разному, и лучше, чтобы это было написано в
 * одном месте и проверялось тестом.
 *
 * @returns {{дельтаМм3: number, процент: number, словами: string}}
 */
export function compareLesion(былоМм3, сталоМм3) {
  const было = Number(былоМм3) || 0;
  const стало = Number(сталоМм3) || 0;
  const дельта = стало - было;
  const процент = было > 0 ? (дельта / было) * 100 : 0;
  const знак = дельта > 0 ? '+' : '';
  const словами = было > 0
    ? `${было.toFixed(0)} → ${стало.toFixed(0)} мм³ (${знак}${процент.toFixed(1)}%)`
    : `${стало.toFixed(0)} мм³`;
  return { дельтаМм3: дельта, процент, словами };
}

/** Диаметр шара такого же объёма — привычнее миллиметры, чем кубические. */
export function equivalentDiameter(объёмМм3) {
  const v = Number(объёмМм3) || 0;
  if (v <= 0) return 0;
  return 2 * Math.cbrt((3 * v) / (4 * Math.PI));
}
